// frontend/src/components/XPHubLetsYou.jsx
import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  Library,
  Star,
  MessageCircle,
  BarChart3,
  ListChecks,
  Newspaper,
  Trophy,
  Brain,
  Bell,
} from "lucide-react";

const CARDS = [
  {
    id: 1,
    Icon: Library,
    title: "Llevar el control de tus juegos",
    text:
      "Registra todos los juegos que has jugado o empieza a construir tu biblioteca desde hoy.",
    anchor: "library",
  },
  {
    id: 2,
    Icon: Star,
    title: "Puntuar y opinar",
    text:
      "Valora tus juegos favoritos con estrellas, deja una reseña y comparte tu experiencia con otros jugadores.",
    anchor: "reviews",
  },
  {
    id: 3,
    Icon: MessageCircle,
    title: "Conectar con otros jugadores",
    text:
      "Descubre qué están jugando tus amigos, comenta sus reseñas y comparte momentos únicos de tu experiencia gamer.",
    anchor: "community",
  },
  {
    id: 4,
    Icon: BarChart3,
    title: "Analizar tu progreso",
    text:
      "Revisa tus estadísticas de juego, tus horas invertidas y tus géneros más jugados. Conoce tu trayectoria gamer.",
    anchor: "progress",
  },
  {
    id: 5,
    Icon: ListChecks,
    title: "Crear listas y colecciones",
    text:
      "Haz tus propias listas: sagas favoritas, pendientes, joyas ocultas o tus mejores del año. ¡Compártelas con la comunidad!",
    anchor: "lists",
  },
  {
    id: 6,
    Icon: Newspaper,
    title: "Mantenerte al día",
    text:
      "Accede a noticias, lanzamientos y tendencias del mundo gamer desde una sola plataforma, sin perderte nada.",
    anchor: "news",
  },
];

// Actívalo a true cuando existan estas features
const SHOW_OPTIONALS = false;
const OPTIONALS = [
  {
    id: "A",
    Icon: Trophy,
    title: "Desbloquear logros",
    text: "Registra tus metas personales y presume tus hitos gamers.",
    anchor: "achievements",
  },
  {
    id: "B",
    Icon: Brain,
    title: "Descubrir nuevos títulos",
    text: "Recibe recomendaciones según tu estilo de juego.",
    anchor: "discover",
  },
  {
    id: "C",
    Icon: Bell,
    title: "Seguir estudios y creadores",
    text: "Mantente al tanto de sus nuevas reseñas o listas.",
    anchor: "follows",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.97 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", duration: 0.45, bounce: 0, delay: i * 0.07 },
  }),
};

export default function XPHubLetsYou() {
  const gridRef = useRef(null);
  const isInView = useInView(gridRef, { once: true, margin: "-60px" });

  return (
    <section className="xp-lets">
      <div className="xp-lets__inner">
        <motion.h2
          className="xp-lets__title"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ type: "spring", duration: 0.4, bounce: 0 }}
        >
          XPHub te permite…
        </motion.h2>

        <div className="xp-lets__grid" ref={gridRef}>
          {CARDS.map(({ id, Icon, title, text, anchor }, i) => (
            <motion.div
              key={id}
              custom={i}
              variants={cardVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              <Link
                to={`/welcome#${anchor}`}
                className="xp-card"
                aria-label={title}
              >
                <div className="xp-card__icon" aria-hidden="true">
                  <Icon size={26} />
                </div>
                <h3 className="xp-card__h">{title}</h3>
                <p className="xp-card__p">{text}</p>
              </Link>
            </motion.div>
          ))}
        </div>

        {SHOW_OPTIONALS && (
          <>
            <h3 className="xp-lets__subtitle">Alternativas opcionales</h3>
            <div className="xp-lets__grid xp-lets__grid--optional">
              {OPTIONALS.map(({ id, Icon, title, text, anchor }) => (
                <Link
                  key={id}
                  to={`/welcome#${anchor}`}
                  className="xp-card xp-card--dim"
                  aria-label={title}
                >
                  <div className="xp-card__icon" aria-hidden="true">
                    <Icon size={24} />
                  </div>
                  <h4 className="xp-card__h">{title}</h4>
                  <p className="xp-card__p">{text}</p>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
